import React, { useState, useEffect } from 'react';
import { Heart, Shield, Zap, Linkedin } from 'lucide-react';

interface Sponsor {
  name: string;
  logoUrl: string;
  website: string;
  order: number;
}

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const response = await fetch('/api/admin/content/public');
        const data = await response.json();
        if (data.success && data.data.sponsors) {
          setSponsors(data.data.sponsors);
        }
      } catch (error) {
        console.error('Failed to fetch sponsors:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSponsors();
  }, []);

  return (
    <footer className="bg-secondary text-white">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-primary-light">Quick Links</h4>
            <ul className="space-y-2 text-gray-300">
              <li><a href="/" className="hover:text-accent transition-colors">Home</a></li>
              <li><a href="/events" className="hover:text-accent transition-colors">Events</a></li>
              <li><a href="/jobs" className="hover:text-accent transition-colors">Jobs</a></li>
              <li><a href="/login" className="hover:text-accent transition-colors">Login</a></li>
              <li><a href="/register" className="hover:text-accent transition-colors">Register</a></li>
              <li><a href="/about-us" className="hover:text-accent transition-colors">About Us</a></li>
            </ul>
          </div>

          {/* Support */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-primary-light">Support</h4>
            <ul className="space-y-2 text-gray-300">
              <li><a href="/contact-us" className="hover:text-accent transition-colors">Contact Us</a></li>
              <li><a href="/support" className="hover:text-accent transition-colors">Report Bugs/Suggestions</a></li>
              <li><a href="/privacy-policy" className="hover:text-accent transition-colors">Privacy Policy</a></li>
              <li><a href="/terms-of-service" className="hover:text-accent transition-colors">Terms of Service</a></li>
              <li><a href="/ai-hackathon-terms" className="hover:text-accent transition-colors">AI Hackathon Terms of Service</a></li>
              <li>
                <a 
                  href="https://www.linkedin.com/company/udyogpramoda-in?source=website" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-accent transition-colors flex items-center"
                >
                  <Linkedin className="w-4 h-4 mr-2" />
                  LinkedIn
                </a>
              </li>
            </ul>
          </div>

          {/* Sponsors */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-primary-light">Powered By</h4>
            <div className="flex flex-col space-y-3">
              {loading ? (
                <div className="text-gray-400 text-sm">Loading sponsors...</div>
              ) : sponsors.length > 0 ? (
                sponsors.map((sponsor, index) => (
                  <div key={index} className="bg-white rounded-lg px-2 py-1 border border-gray-200 flex items-center justify-center shadow-sm w-24">
                    {sponsor.logoUrl ? (
                      <img 
                        src={sponsor.logoUrl} 
                        alt={sponsor.name} 
                        className="w-20 h-8 object-contain"
                        onError={(e) => {
                          // Fallback to colored square with initial if image fails
                          const target = e.currentTarget;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            const fallback = document.createElement('div');
                            fallback.className = 'w-20 h-8 bg-warning rounded flex items-center justify-center';
                            fallback.innerHTML = `<span class="text-white text-sm font-bold">${sponsor.name.charAt(0).toUpperCase()}</span>`;
                            parent.appendChild(fallback);
                          }
                        }}
                      />
                    ) : (
                      <div className="w-20 h-8 bg-warning rounded flex items-center justify-center">
                        <span className="text-white text-sm font-bold">{sponsor.name.charAt(0).toUpperCase()}</span>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                // Fallback to default sponsors if none are configured
                <>
                  <div className="bg-white rounded-lg px-2 py-1 border border-gray-200 flex items-center justify-center shadow-sm w-24">
                    <div className="w-20 h-8 bg-warning rounded flex items-center justify-center">
                      <span className="text-white text-sm font-bold">A</span>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg px-2 py-1 border border-gray-200 flex items-center justify-center shadow-sm w-24">
                    <div className="w-20 h-8 bg-info rounded flex items-center justify-center">
                      <span className="text-white text-sm font-bold">S</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar with Copyright */}
      <div className="border-t border-secondary-light">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              © {currentYear} Udyoga Pramoda. All rights reserved.
            </div>
            <div className="text-gray-400 text-sm">
              Empowering Careers, Building Futures
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 